import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, ArrowRight } from "lucide-react";
import { SITE, MEDIA } from "@/lib/site";
import Seo from "@/components/Seo";

const ANSWER_TOPICS = [
  { label: "I Need a Sign. Where Do I Start?",                  slug: "storefront-signs" },
  { label: "Do I Need a Monument Sign?",                        slug: "monument-signs" },
  { label: "I Need a Sign on My Building. What Should I Know?", slug: "building-signs" },
  { label: "Should I Add Graphics to My Windows?",              slug: "window-graphics" },
  { label: "Do I Need a Sign Permit?",                          slug: "do-i-need-a-sign-permit" },
  { label: "Should I Repair My Sign or Replace It?",            slug: "repair-or-replace" },
];

export default function Home() {
  const aboutRef = useRef(null);

  useEffect(() => {
    const root = aboutRef.current;
    if (!root) return;
    const els = root.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -60px 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div data-testid="home-page" className="bg-white">
      <Seo
        path="/"
        title="Commercial Signs Richmond VA"
        description="Commercial signage help for Richmond businesses. Storefront signs, building signs, monument signs, permitting, surveys and installation. Talk directly with Todd Friedman."
      />
      {/* HERO */}
      <section className="bg-white pt-10 lg:pt-16 pb-20 lg:pb-28" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-8 fade-up">
              <span className="w-10 h-0.5 bg-[#F97316]" />
              <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F97316]">Richmond, Virginia</span>
            </div>
            <h1 className="font-heading text-[#1A1A1A] tracking-tight leading-[0.95] fade-up fade-up-delay-1" style={{ fontWeight: 900, fontSize: "clamp(2.5rem, 5.5vw, 5rem)" }} data-testid="hero-headline">
              Commercial Signs for Richmond Businesses
            </h1>
            <div className="mt-10 space-y-5 text-lg text-[#2B2F33]/85 leading-relaxed max-w-xl fade-up fade-up-delay-2">
              <p>Opening, relocating or updating a business? Todd Friedman helps coordinate storefront signs, building signs, monument signs, surveys, landlord approvals, permitting and installation throughout Richmond and Central Virginia.</p>
            </div>
            <div className="mt-10 flex flex-wrap gap-3 fade-up fade-up-delay-3">
              <Link to="/contact" data-testid="hero-cta-primary" className="bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-bold uppercase tracking-wider px-7 py-4 inline-flex items-center gap-2 transition-colors">
                Discuss Your Sign Project <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={SITE.phoneHref} data-testid="hero-cta-call" className="border-2 border-[#1A1A1A]/15 hover:border-[#1A1A1A] text-[#1A1A1A] text-sm font-bold uppercase tracking-wider px-7 py-4 inline-flex items-center gap-2 transition-colors">
                <Phone className="w-4 h-4" /> Call {SITE.phone}
              </a>
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-6 relative">
            <div className="max-w-md lg:max-w-none mx-auto">
              <img
                src={MEDIA.toddPortrait}
                alt="Todd Friedman, Richmond signage resource"
                className="block w-full h-auto bg-[#F8F8F5]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* BUYER-SITUATION CARDS */}
      <section className="bg-[#F8F8F5] py-14 lg:py-20 border-y border-[#1A1A1A]/8" data-testid="situation-cards">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-10 h-0.5 bg-[#F97316]" />
            <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F97316]">Where To Start</span>
          </div>
          <h2 className="font-heading text-[#1A1A1A] tracking-tight leading-[1.05] max-w-3xl" style={{ fontWeight: 900, fontSize: "clamp(1.875rem, 3.5vw, 2.75rem)" }}>
            What brings you to RVA Signs?
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {[
              { to: "/new-business-signage-richmond-va", label: "Opening a New Location", desc: "Lease to install — the sign process from day one." },
              { to: "/storefront-signs-richmond-va", label: "Replacing an Existing Sign", desc: "Storefronts, channel letters, cabinet signs." },
              { to: "/commercial-signs-richmond-va", label: "Repairing or Updating a Sign", desc: "Faded faces, broken LEDs, tenant panels, refurbishment." },
              { to: "/sign-installation-richmond-va", label: "National Sign Company Needing Local Support", desc: "Surveys, permits, receive-and-install in Central Virginia." },
            ].map((c) => (
              <Link
                key={c.to}
                to={c.to}
                data-testid={`situation-card-${c.to.replace(/^\//, "")}`}
                className="group block bg-white p-7 border border-[#1A1A1A]/8 hover:border-[#1A1A1A] transition-colors"
              >
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#F97316]">Situation</div>
                <h3 className="mt-3 font-heading text-lg text-[#1A1A1A] tracking-tight leading-snug" style={{ fontWeight: 800 }}>
                  {c.label}
                </h3>
                <p className="mt-3 text-sm text-[#2B2F33]/80 leading-relaxed">{c.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] text-[#F97316] group-hover:gap-2.5 transition-all">
                  Learn more <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE HUB LINKS */}
      <section className="bg-white py-16 lg:py-24" data-testid="service-hub">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-10 h-0.5 bg-[#F97316]" />
            <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F97316]">Commercial Sign Services</span>
          </div>
          <h2 className="font-heading text-[#1A1A1A] tracking-tight leading-[1.05] max-w-3xl" style={{ fontWeight: 900, fontSize: "clamp(1.875rem, 3.5vw, 2.75rem)" }}>
            Services across Richmond and Central Virginia.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
            {[
              { to: "/commercial-signs-richmond-va", label: "Commercial Signs in Richmond, VA" },
              { to: "/storefront-signs-richmond-va", label: "Storefront Signs in Richmond, VA" },
              { to: "/monument-signs-richmond-va", label: "Monument Signs in Richmond, VA" },
              { to: "/sign-installation-richmond-va", label: "Sign Installation in Richmond" },
              { to: "/new-business-signage-richmond-va", label: "New Business Signage in Richmond" },
              { to: "/projects", label: "Project Examples" },
            ].map((s) => (
              <Link
                key={s.to}
                to={s.to}
                className="group flex items-center justify-between gap-4 py-5 border-b border-[#1A1A1A]/10 hover:border-[#F97316] transition-colors"
              >
                <span className="font-heading text-lg lg:text-xl text-[#1A1A1A] tracking-tight" style={{ fontWeight: 700 }}>
                  {s.label}
                </span>
                <ArrowUpRight className="w-5 h-5 text-[#1A1A1A]/40 group-hover:text-[#F97316] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT TODD */}
      <section ref={aboutRef} className="bg-white py-24 lg:py-32" data-testid="about-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="hidden lg:block lg:col-span-5">
            <div className="max-w-md lg:max-w-none mx-auto reveal reveal-img">
              <img
                src={MEDIA.toddPortraitBW}
                alt="Todd Friedman"
                className="block w-full h-auto bg-[#F8F8F5]"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5 reveal reveal-delay-1">
              <span className="w-8 h-0.5 bg-[#F97316]" />
              <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F97316]">About Todd</span>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-[#1A1A1A] leading-[1.05] reveal reveal-delay-2" style={{ fontWeight: 900 }}>
              I&rsquo;m Todd.
            </h2>
            <p className="mt-4 font-heading text-2xl text-[#F97316] leading-tight reveal reveal-delay-3" style={{ fontWeight: 700 }}>
              I help businesses with signage.
            </p>
            {/* Mobile/tablet portrait — rounded rectangle, full-width, no awkward cropping */}
            <div className="lg:hidden mt-8" data-testid="about-strip-mobile-portrait">
              <img
                src={MEDIA.toddPortraitBW}
                alt="Todd Friedman"
                className="block w-full h-auto rounded-xl bg-[#F8F8F5] border border-[#1A1A1A]/8"
                loading="lazy"
              />
            </div>
            <div className="mt-8 space-y-4 text-lg text-[#2B2F33]/85 leading-relaxed max-w-xl">
              <p className="reveal reveal-delay-4">I&rsquo;m a Richmond-based sign professional. I help local businesses understand their options, avoid common mistakes, and figure out what needs to happen next.</p>
              <p className="reveal reveal-delay-5">I work alongside the team at <strong className="text-[#1A1A1A] font-semibold">Superior Signs</strong>, one of Richmond&rsquo;s established commercial sign companies. When a project moves into fabrication and installation, I have experienced people I can lean on.</p>
            </div>
            <Link
              to="/about"
              className="mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#F97316] hover:gap-3 transition-all reveal reveal-delay-6"
              data-testid="about-strip-cta"
            >
              Read more about Todd <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SIGN QUESTIONS ANSWERED */}
      <section className="bg-[#F8F8F5] py-20 lg:py-28" data-testid="answers-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-0.5 bg-[#F97316]" />
              <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F97316]">Sign Questions Answered</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#1A1A1A] leading-[1.1]" style={{ fontWeight: 800 }}>
              A few questions I hear all the time.
            </h2>
          </div>

          <div className="bg-white border border-[#1A1A1A]/8 divide-y divide-[#1A1A1A]/8" data-testid="answers-list">
            {ANSWER_TOPICS.map((q, i) => (
              <Link key={q.slug} to={`/resources/${q.slug}`} className="flex items-center justify-between gap-6 p-6 lg:p-7 hover:bg-[#F8F8F5] transition-colors group" data-testid={`answer-row-${i + 1}`}>
                <div className="flex items-center gap-5 min-w-0">
                  <span className="font-heading text-sm text-[#F97316] tracking-widest shrink-0" style={{ fontWeight: 800 }}>{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-heading text-lg lg:text-xl text-[#1A1A1A] tracking-tight truncate" style={{ fontWeight: 700 }}>{q.label}</span>
                </div>
                <ArrowRight className="w-5 h-5 text-[#1A1A1A]/40 group-hover:text-[#F97316] group-hover:translate-x-1 transition-all shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-white py-24 lg:py-32" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="w-8 h-0.5 bg-[#F97316]" />
            <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F97316]">Talk to Todd</span>
            <span className="w-8 h-0.5 bg-[#F97316]" />
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1A1A1A] leading-[1.05]" style={{ fontWeight: 900 }}>
            Still have a question? Ready to get started?
          </h2>
          <p className="mt-6 text-lg text-[#2B2F33]/80 leading-relaxed max-w-xl mx-auto">
            Give me a call. I&rsquo;ll help you figure out what needs to happen next.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <a href={SITE.phoneHref} className="bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-bold uppercase tracking-wider px-7 py-4 inline-flex items-center gap-2 transition-colors" data-testid="final-cta-phone">
              <Phone className="w-4 h-4" /> {SITE.phone}
            </a>
            <Link to="/contact" className="border-2 border-[#1A1A1A]/15 hover:border-[#1A1A1A] text-[#1A1A1A] text-sm font-bold uppercase tracking-wider px-7 py-4 inline-flex items-center gap-2 transition-colors" data-testid="final-cta-form">
              Send a Message <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export function CtaBand() {
  return (
    <section className="bg-white py-20 lg:py-28 border-t border-[#1A1A1A]/8" data-testid="cta-band">
      <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-5">
          <span className="w-8 h-0.5 bg-[#F97316]" />
          <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F97316]">Talk to Todd</span>
          <span className="w-8 h-0.5 bg-[#F97316]" />
        </div>
        <h3 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#1A1A1A] leading-[1.1]" style={{ fontWeight: 800 }}>
          Still have a question? Ready to get started?
        </h3>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a href={SITE.phoneHref} className="bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-bold uppercase tracking-wider px-6 py-3.5 inline-flex items-center gap-2 transition-colors" data-testid="cta-band-primary">
            <Phone className="w-4 h-4" /> {SITE.phone}
          </a>
          <Link to="/contact" className="border-2 border-[#1A1A1A]/15 hover:border-[#1A1A1A] text-[#1A1A1A] text-sm font-bold uppercase tracking-wider px-6 py-3.5 inline-flex items-center gap-2 transition-colors">
            Send a Message <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
